import { Accordion, AccordionItem, Button, Flex, HStack, Stack, useColorModeValue } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { LoadingSpinner } from 'components/LoadingSpinner';
import FormEmbeddedAlertModal from 'components/modals/FormEmbeddedAlertModal';
import { StatusTag } from 'components/StatusTag';
import { useUserContext } from 'context/UserContextProvider';
import { getLightStatusColor } from 'helpers/getColourSchemeBasedOnStatus';
import { dropdown } from 'helpers/makeFormFields';
import { resolveStatusName } from 'helpers/statusTranslations';
import { get } from 'lodash';
import { getI18n, useTranslation } from 'react-i18next';
import * as yup from 'yup';

const RESOURCE_KEY = 'b2c_service_history_request';

export const B2CServiceHistoryStatusCard = (props: {
  permission: string;
  canEdit?: boolean;
  completedAt?: string;
  completedTime?: string | null | undefined;
  currentStatus: string;
  id?: number;
  isLoading: boolean;
  reasons?: any;
  shouldHaveFile?: boolean;
  updateStatus?: (data: any) => void;
  assignee: any;
}) => {
  const { t } = useTranslation();
  const { permission, canEdit = true, currentStatus, isLoading, updateStatus } = props;
  const { isMember, statusCodes } = useUserContext();
  const statusName = resolveStatusName(get(statusCodes, RESOURCE_KEY), currentStatus);

  const schema = yup
    .object({
      status: yup.string().max(255).required(),
    })
    .required();

  const isAllowed = isMember(permission);

  return (
    <Stack
      direction={'column'}
      w={'100%'}
      bgColor={getLightStatusColor(statusName)}
      p={6}
      borderRadius="lg"
      boxShadow={useColorModeValue('md', 'md-dark')}
      pos="relative"
      data-test-id="status-card"
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Accordion allowToggle>
          <AccordionItem border="none">
            <HStack justifyContent="space-between">
              <StatusTag resourceKey={RESOURCE_KEY} currentStatus={currentStatus} isCell />
              <Flex>
                {canEdit && (
                  <Button
                    disabled={!isAllowed}
                    variant="primary"
                    data-test-id="status-update-button"
                    onClick={() => {
                      NiceModal.show(FormEmbeddedAlertModal as any, {
                        content: {
                          header: t('components:update_status_modal.header'),
                          formTestId: 'status-form',
                          formFields: (props: { control: any; register: any; errors: any; watch: any; watchedFields: { status: number } }) => {
                            const { control, errors } = props;

                            return (
                              <>
                                {dropdown({
                                  name: 'status',
                                  label: t('components:update_status_modal.status.label'),
                                  // @ts-ignore
                                  control,
                                  errors,
                                  options: [
                                    {
                                      label: getI18n().t('components:update_status_modal.transitions.choose'),
                                      value: 'unknown',
                                    },
                                    {
                                      label: getI18n().t('b2c_service_history_request:status_update.options.pending_payment'),
                                      value: 'pending_payment',
                                    },
                                    {
                                      label: getI18n().t('b2c_service_history_request:status_update.options.canceled_invalid_vin'),
                                      value: 'canceled_invalid_vin',
                                    },
                                    {
                                      label: getI18n().t('b2c_service_history_request:status_update.options.canceled_no_dsb_portal'),
                                      value: 'canceled_no_dsb_portal',
                                    },
                                  ],
                                })}
                              </>
                            );
                          },
                          footer: {
                            buttons: {
                              formButtonTestId: 'scan-submit-button',
                              actionCaption: t('common:edit'),
                            },
                          },
                        },
                        onMutate: updateStatus,
                        schema,
                      });
                    }}
                  >
                    {t('common:edit')}
                  </Button>
                )}
              </Flex>
            </HStack>
          </AccordionItem>
        </Accordion>
      )}
    </Stack>
  );
};
